import React, { useState } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  OutlinedInput, 
  Chip, 
  Checkbox, 
  ListItemText, 
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

export interface MultiSelectOption {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedIds,
  onChange,
  placeholder = 'Select items...',
  disabled = false,
  error = false,
  helperText
}) => {
  const [open, setOpen] = useState(false);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onChange(typeof value === 'string' ? value.split(',') : value);
  };

  const getSelectedLabels = () => {
    return selectedIds
      .map(id => options.find(opt => opt.id === id)?.label)
      .filter(Boolean) as string[];
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return <span style={{ color: '#999' }}>{placeholder}</span>;
    }
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {getSelectedLabels().map((label) => (
          <Chip key={label} label={label} size="small" />
        ))}
      </Box>
    );
  };

  return (
    <FormControl fullWidth error={error}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={selectedIds}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        IconComponent={() => (
          <FontAwesomeIcon 
            icon={faChevronDown} 
            style={{ 
              marginRight: '8px', 
              fontSize: '14px',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} 
          />
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: 'auto',
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            <Checkbox checked={selectedIds.includes(option.id)} />
            {option.icon && (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                {option.icon}
              </Box>
            )}
            <Box sx={{ flexGrow: 1 }}>
              <ListItemText 
                primary={option.label}
                secondary={option.description}
              />
            </Box>
          </MenuItem>
        ))}
        {options.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No options available" />
          </MenuItem>
        )}
      </Select>
      {helperText && (
        <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: error ? 'error.main' : 'text.secondary' }}>
          {helperText}
        </Box>
      )}
    </FormControl>
  );
};

export default MultiSelectDropdown;